/* eslint-disable react-refresh/only-export-components */
import { formatHumanName, ProfileResource } from '@medplum/core';
import { Document, Page, Text, View, StyleSheet, pdf, Image } from '@react-pdf/renderer';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';
import everselfName from '@/assets/everself_bw.png';

interface CreateFaxFileParams {
  content: string;
  subject: string;
  recipient?: string;
  recipientNumber: string | null;
  profile: ProfileResource;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
  },
  logoLetter: {
    height: 24,
    marginRight: 10,
  },
  letterhead: {
    alignItems: 'flex-end',
    margin: 24,
  },
  letterheadContent: {
    marginTop: 10,
    marginRight: 10,
    alignItems: 'flex-end',
  },
  letterheadLogo: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
  letterheadText: {
    fontSize: 10,
    textAlign: 'right',
  },
  section: {
    margin: 10,
    padding: 10,
  },
  coverSheetTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  columnsContainer: {
    flexDirection: 'row',
    marginTop: 10,
  },
  firstColumn: {
    flex: 1,
    marginRight: 20,
  },
  secondColumn: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  fieldValue: {
    fontSize: 10,
  },
  content: {
    fontSize: 10,
  },
});

const FaxFile = ({
  content,
  subject,
  recipient,
  recipientNumber,
  sender,
  date,
  senderEmail,
}: {
  content: string;
  subject: string;
  recipient?: string;
  recipientNumber: string | null;
  sender: string;
  date: string;
  senderEmail?: string;
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.letterhead}>
        <View style={styles.letterheadLogo}>
          <Image style={styles.logoLetter} src={everselfName} />
        </View>
        <View style={styles.letterheadContent}>
          <Text style={styles.letterheadText}>Everself</Text>
          <Text style={styles.letterheadText}>{senderEmail}</Text>
          <Text style={styles.letterheadText}>{sender}</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.coverSheetTitle}>FAX COVER SHEET</Text>

        <View style={styles.columnsContainer}>
          <View style={styles.firstColumn}>
            <Text>
              <Text style={styles.fieldLabel}>Date: </Text>
              <Text style={styles.fieldValue}>{date}</Text>
            </Text>
            <Text>
              <Text style={styles.fieldLabel}>To: </Text>
              <Text style={styles.fieldValue}>{recipient}</Text>
            </Text>
            <Text>
              <Text style={styles.fieldLabel}>Destination: </Text>
              <Text style={styles.fieldValue}>{recipientNumber ? formatPhoneNumber(recipientNumber) : ''}</Text>
            </Text>
            <Text>
              <Text style={styles.fieldLabel}>Subject: </Text>
              <Text style={styles.fieldValue}>{subject}</Text>
            </Text>
          </View>

          <View style={styles.secondColumn}>
            <Text>
              <Text style={styles.fieldLabel}>From: </Text>
              <Text style={styles.fieldValue}>{sender}</Text>
            </Text>
            <Text>
              <Text style={styles.fieldLabel}>Contact Phone: </Text>
              <Text style={styles.fieldValue}>
                {import.meta.env?.VITE_BARIENDO_CONTACT_NUMBER
                  ? formatPhoneNumber(import.meta.env?.VITE_BARIENDO_CONTACT_NUMBER)
                  : ''}
              </Text>
            </Text>
            <Text>
              <Text style={styles.fieldLabel}>Contact Fax: </Text>
              <Text style={styles.fieldValue}>
                {import.meta.env?.VITE_BARIENDO_FAX_NUMBER
                  ? formatPhoneNumber(import.meta.env?.VITE_BARIENDO_FAX_NUMBER)
                  : ''}
              </Text>
            </Text>
          </View>
        </View>
      </View>
      <View style={{ ...styles.section, flexGrow: 1 }}>
        <Text style={styles.content}>{content}</Text>
      </View>
    </Page>
  </Document>
);

const createFaxFile = async ({
  content,
  subject,
  recipient,
  recipientNumber,
  profile,
}: CreateFaxFileParams): Promise<File> => {
  const pdfBlob = await pdf(
    <FaxFile
      content={content}
      subject={subject}
      recipient={recipient}
      recipientNumber={recipientNumber}
      sender={formatHumanName(profile.name?.[0])}
      date={new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/New_York',
        timeZoneName: 'short',
      })}
      senderEmail={profile.telecom?.find((telecom) => telecom.system === 'email')?.value}
    />
  ).toBlob();
  return new File([pdfBlob], 'fax.pdf', { type: 'application/pdf' });
};

export default createFaxFile;
